'use client';

import React, { useState, useEffect } from 'react';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface SequenceStep {
  id: string;
  nameId: string;
  nameAr: string;
  nameTranslit: string;
  nameMeaning: string;
  note: string;
  color: string;
}

interface PersonalSequence {
  id: string;
  title: string;
  description: string;
  steps: SequenceStep[];
  createdAt: string;
  context?: string;
}

// ─── AVAILABLE NAMES (subset for sequence building) ──────────────────────────

const SEQUENCE_NAMES = [
  { id: 'qadir', ar: 'الْقَادِر', translit: 'Al-Qadir', meaning: 'The All-Powerful', color: '#c9a84c' },
  { id: 'khaliq', ar: 'الْخَالِق', translit: 'Al-Khaliq', meaning: 'The Creator', color: '#c9a84c' },
  { id: 'bari', ar: 'الْبَارِئ', translit: 'Al-Bari', meaning: 'The Evolver', color: '#2d7a47' },
  { id: 'musawwir', ar: 'الْمُصَوِّر', translit: 'Al-Musawwir', meaning: 'The Fashioner', color: '#2d7a47' },
  { id: 'razzaq', ar: 'الرَّزَّاق', translit: 'Ar-Razzaq', meaning: 'The Provider', color: '#2d8a87' },
  { id: 'nur', ar: 'النُّور', translit: 'An-Nur', meaning: 'The Light', color: '#c9a84c' },
  { id: 'latif', ar: 'اللَّطِيف', translit: 'Al-Latif', meaning: 'The Subtle', color: '#8a8ab8' },
  { id: 'khabir', ar: 'الْخَبِير', translit: 'Al-Khabir', meaning: 'The Aware', color: '#8a8ab8' },
  { id: 'basir', ar: 'الْبَصِير', translit: 'Al-Basir', meaning: 'The All-Seeing', color: '#2d8a87' },
  { id: 'alim', ar: 'الْعَلِيم', translit: 'Al-Alim', meaning: 'The All-Knowing', color: '#D4A82A' },
  { id: 'zahir', ar: 'الظَّاهِر', translit: 'Az-Zahir', meaning: 'The Manifest', color: '#c9a84c' },
  { id: 'batin', ar: 'الْبَاطِن', translit: 'Al-Batin', meaning: 'The Hidden', color: '#8a8ab8' },
  { id: 'hadi', ar: 'الْهَادِي', translit: 'Al-Hadi', meaning: 'The Guide', color: '#2d7a47' },
  { id: 'wadud', ar: 'الْوَدُود', translit: 'Al-Wadud', meaning: 'The Loving', color: '#c9a84c' },
  { id: 'adl', ar: 'الْعَدْل', translit: 'Al-Adl', meaning: 'The Just', color: '#D4A82A' },
  { id: 'hafiz', ar: 'الْحَفِيظ', translit: 'Al-Hafiz', meaning: 'The Preserver', color: '#2d7a47' },
  { id: 'fattah', ar: 'الْفَتَّاح', translit: 'Al-Fattah', meaning: 'The Opener', color: '#2d8a87' },
  { id: 'karim', ar: 'الْكَرِيم', translit: 'Al-Karim', meaning: 'The Generous', color: '#c9a84c' },
  { id: 'rahman', ar: 'الرَّحْمَن', translit: 'Ar-Rahman', meaning: 'The Most Merciful', color: '#2d7a47' },
  { id: 'rahim', ar: 'الرَّحِيم', translit: 'Ar-Rahim', meaning: 'The Especially Merciful', color: '#2d7a47' },
];

const STORAGE_KEY = 'haal_sunnat_sequences';

// ─── STEP CARD ────────────────────────────────────────────────────────────────

function StepCard({
  step,
  index,
  onRemove,
  onNoteChange,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  step: SequenceStep;
  index: number;
  onRemove: () => void;
  onNoteChange: (note: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'flex-start',
      padding: '12px 14px',
      background: `${step.color}08`,
      border: `1px solid ${step.color}25`,
      borderRadius: 8,
      position: 'relative',
    }}>
      {/* Step number */}
      <div style={{
        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
        background: `${step.color}15`, border: `1px solid ${step.color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, color: step.color, fontFamily: 'monospace', fontWeight: 'bold',
      }}>
        {index + 1}
      </div>

      {/* Name info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'serif', fontSize: 16, color: step.color }}>{step.nameAr}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#F5F0E8', fontFamily: 'monospace' }}>{step.nameTranslit}</span>
          <span style={{ fontSize: 9, color: 'rgba(212,197,169,0.4)', fontFamily: 'monospace' }}>{step.nameMeaning}</span>
        </div>
        <textarea
          value={step.note}
          onChange={e => onNoteChange(e.target.value)}
          placeholder="Why this Name at this step? What does it reveal in this sequence?"
          rows={2}
          style={{
            width: '100%', background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4,
            padding: '6px 8px', color: 'rgba(212,197,169,0.7)', fontSize: 10,
            resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5,
          }}
        />
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
        <button onClick={onMoveUp} disabled={isFirst} style={{ fontSize: 10, padding: '2px 6px', background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, cursor: isFirst ? 'not-allowed' : 'pointer', color: isFirst ? 'rgba(212,197,169,0.15)' : 'rgba(212,197,169,0.4)' }}>↑</button>
        <button onClick={onMoveDown} disabled={isLast} style={{ fontSize: 10, padding: '2px 6px', background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, cursor: isLast ? 'not-allowed' : 'pointer', color: isLast ? 'rgba(212,197,169,0.15)' : 'rgba(212,197,169,0.4)' }}>↓</button>
        <button onClick={onRemove} style={{ fontSize: 10, padding: '2px 6px', background: 'rgba(255,80,80,0.06)', border: '1px solid rgba(255,80,80,0.15)', borderRadius: 3, cursor: 'pointer', color: 'rgba(255,100,100,0.5)' }}>×</button>
      </div>
    </div>
  );
}

// ─── SEQUENCE TIMELINE DISPLAY ────────────────────────────────────────────────

function SequenceTimeline({ sequence }: { sequence: PersonalSequence }) {
  return (
    <div style={{ padding: '14px 16px', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8 }}>
      <div style={{ marginBottom: 10 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#F5F0E8', marginBottom: 2 }}>{sequence.title}</h4>
        {sequence.description && <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.5)', lineHeight: 1.4 }}>{sequence.description}</p>}
        {sequence.context && <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.35)', fontStyle: 'italic', marginTop: 3 }}>{sequence.context}</p>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
        <div style={{ position: 'absolute', left: 13, top: 14, bottom: 14, width: 1, background: 'rgba(201,168,76,0.1)' }} />
        {sequence.steps.map((step, i) => (
          <div key={step.id} style={{ display: 'flex', gap: 10, paddingBottom: i < sequence.steps.length - 1 ? 10 : 0 }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, background: `${step.color}12`, border: `1px solid ${step.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: step.color, fontFamily: 'monospace', zIndex: 1 }}>
              {i + 1}
            </div>
            <div style={{ flex: 1, paddingTop: 3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ fontFamily: 'serif', fontSize: 13, color: step.color }}>{step.nameAr}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#F5F0E8', fontFamily: 'monospace' }}>{step.nameTranslit}</span>
                <span style={{ fontSize: 8, color: 'rgba(212,197,169,0.35)', fontFamily: 'monospace' }}>{step.nameMeaning}</span>
              </div>
              {step.note && <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', lineHeight: 1.5, fontStyle: 'italic' }}>{step.note}</p>}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(201,168,76,0.08)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 8, color: 'rgba(212,197,169,0.3)', fontFamily: 'monospace' }}>
          {sequence.steps.length} Names · {new Date(sequence.createdAt).toLocaleDateString()}
        </span>
        <span style={{ fontSize: 10, color: '#c9a84c', marginLeft: 'auto' }}>الْحَمْدُ لِلَّه</span>
      </div>
    </div>
  );
}

// ─── MAIN SUNNAT SEQUENCE BUILDER ─────────────────────────────────────────────

export default function SunnatSequenceBuilder() {
  const [sequences, setSequences] = useState<PersonalSequence[]>([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [context, setContext] = useState('');
  const [steps, setSteps] = useState<SequenceStep[]>([]);
  const [nameSearch, setNameSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  // Load from localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try { setSequences(JSON.parse(saved)); } catch { /* silent */ }
      }
    }
  }, []);

  function saveToStorage(seqs: PersonalSequence[]) {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seqs));
    }
  }

  function addStep(name: typeof SEQUENCE_NAMES[0]) {
    const newStep: SequenceStep = {
      id: `step-${Date.now()}-${Math.random()}`,
      nameId: name.id,
      nameAr: name.ar,
      nameTranslit: name.translit,
      nameMeaning: name.meaning,
      note: '',
      color: name.color,
    };
    setSteps(prev => [...prev, newStep]);
  }

  function removeStep(id: string) {
    setSteps(prev => prev.filter(s => s.id !== id));
  }

  function updateStepNote(id: string, note: string) {
    setSteps(prev => prev.map(s => s.id === id ? { ...s, note } : s));
  }

  function moveStep(id: string, dir: 'up' | 'down') {
    setSteps(prev => {
      const arr = [...prev];
      const idx = arr.findIndex(s => s.id === id);
      if (dir === 'up' && idx > 0) [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      if (dir === 'down' && idx < arr.length - 1) [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return arr;
    });
  }

  function saveSequence() {
    if (!title.trim() || steps.length === 0) return;
    const seq: PersonalSequence = {
      id: editingId || `seq-${Date.now()}`,
      title: title.trim(),
      description: description.trim(),
      context: context.trim() || undefined,
      steps,
      createdAt: editingId ? (sequences.find(s => s.id === editingId)?.createdAt || new Date().toISOString()) : new Date().toISOString(),
    };
    const updated = editingId
      ? sequences.map(s => s.id === editingId ? seq : s)
      : [seq, ...sequences];
    setSequences(updated);
    saveToStorage(updated);
    resetForm();
  }

  function deleteSequence(id: string) {
    if (!confirm('Delete this sequence?')) return;
    const updated = sequences.filter(s => s.id !== id);
    setSequences(updated);
    saveToStorage(updated);
  }

  function editSequence(seq: PersonalSequence) {
    setEditingId(seq.id);
    setTitle(seq.title);
    setDescription(seq.description);
    setContext(seq.context || '');
    setSteps(seq.steps);
    setShowBuilder(true);
  }

  function resetForm() {
    setTitle(''); setDescription(''); setContext(''); setSteps([]);
    setEditingId(null); setShowBuilder(false); setNameSearch('');
  }

  function exportSequence(seq: PersonalSequence) {
    const text = `SUNNAT SEQUENCE: ${seq.title}\n${seq.description ? seq.description + '\n' : ''}\n${seq.steps.map((s, i) => `${i + 1}. ${s.nameTranslit} (${s.nameAr}) — ${s.nameMeaning}${s.note ? '\n   ' + s.note : ''}`).join('\n\n')}\n\nالْحَمْدُ لِلَّه — Kulli Haal`;
    navigator.clipboard.writeText(text).then(() => alert('Sequence copied to clipboard'));
  }

  const filteredNames = SEQUENCE_NAMES.filter(n =>
    !nameSearch || n.translit.toLowerCase().includes(nameSearch.toLowerCase()) || n.meaning.toLowerCase().includes(nameSearch.toLowerCase())
  );

  return (
    <section className="py-12" style={{ borderTop: '1px solid rgba(138,138,184,0.15)' }}>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5" style={{ background: 'rgba(138,138,184,0.12)', border: '1px solid rgba(138,138,184,0.35)', color: '#8a8ab8' }}>
                ✦ Sunnat Sequence Builder
              </span>
              <span className="font-mono text-[8px] tracking-widest uppercase" style={{ color: 'rgba(138,138,184,0.5)' }}>Personal Timelines</span>
            </div>
            <h2 className="font-mono text-sm font-black uppercase tracking-wide mb-1" style={{ color: '#F5F0E8' }}>
              Compose Your Name Sequences
            </h2>
            <p className="font-mono text-[10px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)', maxWidth: 560 }}>
              Build personal Sunnat timelines — compose your own sequences of Names and save them as personal recognition arcs. Each sequence is a map of how you understand the Order of Operations (Sunnat Allah) in a specific encounter.
            </p>
          </div>
          <button
            onClick={() => { resetForm(); setShowBuilder(v => !v); }}
            className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded transition-all"
            style={{
              background: showBuilder ? 'rgba(138,138,184,0.12)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${showBuilder ? 'rgba(138,138,184,0.4)' : 'rgba(255,255,255,0.1)'}`,
              color: showBuilder ? '#8a8ab8' : 'rgba(245,240,232,0.4)',
            }}
          >
            {showBuilder ? '✕ Cancel' : '⊕ New Sequence'}
          </button>
        </div>

        {/* Builder */}
        {showBuilder && (
          <div className="mb-8 p-5" style={{ background: 'rgba(28,43,62,0.5)', border: '1px solid rgba(138,138,184,0.2)', borderRadius: 10 }}>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(138,138,184,0.6)' }}>
              {editingId ? 'Edit Sequence' : 'Build New Sequence'}
            </p>

            {/* Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div className="md:col-span-2">
                <label className="font-mono text-[8px] uppercase tracking-widest block mb-1.5" style={{ color: 'rgba(245,240,232,0.3)' }}>Sequence Title *</label>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. 'Rain Sequence — from cloud to earth'" className="w-full font-mono text-xs focus:outline-none" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: '8px 12px', color: '#F5F0E8' }} />
              </div>
              <div>
                <label className="font-mono text-[8px] uppercase tracking-widest block mb-1.5" style={{ color: 'rgba(245,240,232,0.3)' }}>Description</label>
                <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description of this sequence..." className="w-full font-mono text-xs focus:outline-none" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: '8px 12px', color: '#F5F0E8' }} />
              </div>
              <div>
                <label className="font-mono text-[8px] uppercase tracking-widest block mb-1.5" style={{ color: 'rgba(245,240,232,0.3)' }}>Context / Encounter</label>
                <input type="text" value={context} onChange={e => setContext(e.target.value)} placeholder="e.g. 'Observed at Humboldt Park, Chicago'" className="w-full font-mono text-xs focus:outline-none" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: '8px 12px', color: '#F5F0E8' }} />
              </div>
            </div>

            {/* Name picker */}
            <div className="mb-5">
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(245,240,232,0.3)' }}>Add Names to Sequence</label>
              <input type="text" value={nameSearch} onChange={e => setNameSearch(e.target.value)} placeholder="Search names..." className="w-full font-mono text-xs focus:outline-none mb-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4, padding: '6px 10px', color: '#F5F0E8' }} />
              <div className="flex flex-wrap gap-2">
                {filteredNames.map(name => (
                  <button
                    key={name.id}
                    onClick={() => addStep(name)}
                    className="flex items-center gap-1.5 transition-all"
                    style={{
                      fontSize: 9, fontFamily: 'monospace', letterSpacing: '.04em',
                      padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                      background: `${name.color}10`,
                      border: `1px solid ${name.color}30`,
                      color: name.color,
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${name.color}20`; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = `${name.color}10`; }}
                  >
                    <span style={{ fontFamily: 'serif', fontSize: 11 }}>{name.ar}</span>
                    {name.translit}
                  </button>
                ))}
              </div>
            </div>

            {/* Steps */}
            {steps.length > 0 && (
              <div className="mb-5">
                <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(245,240,232,0.3)' }}>
                  Sequence ({steps.length} Names) — Drag to reorder
                </label>
                <div className="flex flex-col gap-2">
                  {steps.map((step, i) => (
                    <StepCard
                      key={step.id}
                      step={step}
                      index={i}
                      onRemove={() => removeStep(step.id)}
                      onNoteChange={note => updateStepNote(step.id, note)}
                      onMoveUp={() => moveStep(step.id, 'up')}
                      onMoveDown={() => moveStep(step.id, 'down')}
                      isFirst={i === 0}
                      isLast={i === steps.length - 1}
                    />
                  ))}
                </div>
              </div>
            )}

            {steps.length === 0 && (
              <div className="mb-5 p-4 text-center" style={{ border: '1px dashed rgba(138,138,184,0.2)', borderRadius: 6 }}>
                <p className="font-mono text-[10px]" style={{ color: 'rgba(245,240,232,0.25)' }}>Click Names above to add them to your sequence</p>
              </div>
            )}

            {/* Save */}
            <div className="flex justify-end gap-3">
              <button onClick={resetForm} className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded" style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.4)', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={saveSequence}
                disabled={!title.trim() || steps.length === 0}
                className="font-mono text-[9px] uppercase tracking-widest px-5 py-2 rounded transition-all"
                style={{
                  background: title.trim() && steps.length > 0 ? 'rgba(138,138,184,0.15)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${title.trim() && steps.length > 0 ? 'rgba(138,138,184,0.4)' : 'rgba(255,255,255,0.08)'}`,
                  color: title.trim() && steps.length > 0 ? '#8a8ab8' : 'rgba(245,240,232,0.25)',
                  cursor: title.trim() && steps.length > 0 ? 'pointer' : 'not-allowed',
                }}
              >
                ✦ Save Sequence
              </button>
            </div>
          </div>
        )}

        {/* Saved Sequences */}
        {sequences.length > 0 ? (
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(245,240,232,0.3)' }}>
              Your Personal Timelines ({sequences.length})
            </p>
            <div className="flex flex-col gap-4">
              {sequences.map(seq => (
                <div key={seq.id}>
                  <SequenceTimeline sequence={seq} />
                  <div className="flex gap-3 mt-2 justify-end">
                    <button onClick={() => editSequence(seq)} className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded" style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(245,240,232,0.35)', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => exportSequence(seq)} className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded" style={{ background: 'transparent', border: '1px solid rgba(138,138,184,0.2)', color: 'rgba(138,138,184,0.6)', cursor: 'pointer' }}>⬡ Copy</button>
                    <button onClick={() => deleteSequence(seq.id)} className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded" style={{ background: 'transparent', border: '1px solid rgba(255,80,80,0.15)', color: 'rgba(255,100,100,0.4)', cursor: 'pointer' }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !showBuilder ? (
          <div className="p-8 text-center" style={{ border: '1px dashed rgba(138,138,184,0.15)', borderRadius: 8 }}>
            <div className="text-2xl mb-3" style={{ opacity: 0.2 }}>✦</div>
            <p className="font-mono text-xs mb-2" style={{ color: 'rgba(245,240,232,0.3)' }}>No personal sequences yet</p>
            <p className="font-mono text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>
              Compose your first Name sequence — a personal map of how you recognize the divine Order of Operations
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
